/**
 * BlogPostPage — single-post detail view with reading progress, article
 * JSON-LD, related posts.
 *
 * The route harness passes `slug` (from useParams) + `locale`. The
 * component fetches the post via `client.resolveBlogPost`, renders the
 * body, and renders a "Related posts" panel sourced from
 * `client.listBlogPosts`.
 *
 * @module @puffin-core/marketing-shell/blog/BlogPostPage
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReadingProgress from './ReadingProgress.jsx';
import RelatedPosts from './RelatedPosts.jsx';
import BlogPostFeaturedMedia from './BlogPostFeaturedMedia.jsx';
import { buildBlogPostingJsonLd } from './buildBlogJsonLd.js';
import { DEFAULT_BLOG_UI_STRINGS } from './blogUiStrings.js';
import formatPublishedDate from './formatPublishedDate.js';

const STATE_LOADING = 'loading';
const STATE_READY = 'ready';
const STATE_ERROR = 'error';
const STATE_NOT_FOUND = 'not_found';

/**
 * useRelatedPosts — the related-posts half of a post page: the candidates, whether their read
 * FAILED, the ONE read (`readRelated(isAlive)`, used by the page load and by a retry) and a retry
 * that re-reads only the related posts, leaving the article on screen as it is.
 *
 * A failed read used to be stored as `[]` — "no related posts", the panel vanished — and nothing
 * said so (SWALLOWED-CATCH-BURNDOWN-001 U3). It is now `relatedUnavailable`.
 *
 * @returns {{related: Array, relatedUnavailable: boolean, readRelated: Function,
 *            resetRelatedRefusal: Function, retryRelated: Function}}
 */
function useRelatedPosts({ client, locale, initialRelated }) {
  const [related, setRelated] = useState(initialRelated ?? []);
  const [relatedUnavailable, setRelatedUnavailable] = useState(false);
  const [relatedReloadToken, setRelatedReloadToken] = useState(0);

  // MLC-001: related posts must be in the SAME locale as the article being read, otherwise an `es`
  // post would surface `en` "related" cards.
  const readRelated = useCallback(
    async (isAlive) => {
      try {
        const candidates = await client.listBlogPosts({ limit: 10, locale });
        if (!isAlive()) return;
        setRelated(candidates);
        setRelatedUnavailable(false);
      } catch {
        if (isAlive()) setRelatedUnavailable(true);
      }
    },
    [client, locale],
  );

  useEffect(() => {
    if (relatedReloadToken === 0) return undefined;
    let alive = true;
    readRelated(() => alive);
    return () => {
      alive = false;
    };
  }, [relatedReloadToken, readRelated]);

  const resetRelatedRefusal = useCallback(() => setRelatedUnavailable(false), []);
  const retryRelated = useCallback(() => setRelatedReloadToken((n) => n + 1), []);

  return { related, relatedUnavailable, readRelated, resetRelatedRefusal, retryRelated };
}

/**
 * useBlogPost — resolves a single post (plus, optionally, its related-post
 * candidates) and drives the loading/ready/not-found/error machine. Extracted
 * so <BlogPostPage> is left as a pure render of the resolved article.
 *
 * `relatedUnavailable` is true when the related-posts read FAILED. It used to be read as "no
 * related posts" (the panel vanished, exactly as for a post with none) — SWALLOWED-CATCH-BURNDOWN-001
 * U3. The article still renders; the panel says the read failed and offers `retryRelated`, which
 * re-reads only the related posts.
 *
 * @returns {{state: string, data: object|null, related: Array, relatedUnavailable: boolean,
 *            error: Error|null, retry: Function, retryRelated: Function}}
 */
function useBlogPost({ client, slug, locale, initialData, initialRelated, showRelatedPosts }) {
  const [state, setState] = useState(initialData ? STATE_READY : STATE_LOADING);
  const [data, setData] = useState(initialData ?? null);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const { related, relatedUnavailable, readRelated, resetRelatedRefusal, retryRelated } =
    useRelatedPosts({ client, locale, initialRelated });
  // BLOG-STD-001: when the page is server-rendered by the prerender harness,
  // the first client render is already seeded from `initialData`, so skip the
  // redundant initial fetch to avoid a hydration loading-flash. Client-side
  // navigation (slug/locale change) and explicit retry still fetch normally.
  const seededRef = useRef(Boolean(initialData));

  useEffect(() => {
    if (seededRef.current) {
      seededRef.current = false;
      return undefined;
    }
    let alive = true;
    const isAlive = () => alive;
    setState(STATE_LOADING);
    setError(null);
    resetRelatedRefusal();

    async function load() {
      const post = await client.resolveBlogPost({ slug, locale });
      if (!alive) return;
      if (!post) {
        setState(STATE_NOT_FOUND);
        return;
      }
      setData(post);
      setState(STATE_READY);
      if (showRelatedPosts) {
        await readRelated(isAlive);
      }
    }
    load().catch((err) => {
      if (!alive) return;
      setError(err);
      setState(STATE_ERROR);
    });
    return () => {
      alive = false;
    };
  }, [client, slug, locale, showRelatedPosts, reloadToken, readRelated, resetRelatedRefusal]);

  const retry = useCallback(() => setReloadToken((n) => n + 1), []);

  return { state, data, related, relatedUnavailable, error, retry, retryRelated };
}

/**
 * One blog post's page. Props are true to `BlogPostPage.propTypes` (PROTECTOR-SMS-001 U1b: the server render through
 * `blogPageElementFor` passes only what it has, so an optional prop must read as optional).
 *
 * @param {Object} props
 * @param {{resolveBlogPost: Function, listBlogPosts: Function}} props.client
 * @param {string} props.slug
 * @param {string} [props.locale]
 * @param {Object} [props.initialData] - the post the server resolved (its seed)
 * @param {Array} [props.initialRelated]
 * @param {import('react').ReactNode} [props.loadingFallback=null]
 * @param {import('react').ReactNode} [props.notFoundFallback]
 * @param {Function} [props.errorFallback] - `({ error, retry }) => node`
 * @param {string} [props.hrefPrefix='/blog']
 * @param {string} [props.readingMinutesLabel] - else the blog's strings
 * @param {boolean} [props.showReadingProgress=true]
 * @param {boolean} [props.showRelatedPosts=true]
 * @param {string} [props.relatedPostsHeading] - else the blog's strings
 * @param {Function} [props.renderBody] - renders the post body; else the default body
 * @param {Object} [props.strings] - the blog's chrome strings (`getBlogUiStrings`); English by default
 * @returns {import('react').ReactNode} the page, or the fallback its state calls for
 */
export default function BlogPostPage({
  client,
  slug,
  locale,
  initialData,
  initialRelated,
  loadingFallback = null,
  notFoundFallback,
  errorFallback,
  hrefPrefix = '/blog',
  readingMinutesLabel,
  showReadingProgress = true,
  showRelatedPosts = true,
  relatedPostsHeading,
  renderBody,
  strings,
}) {
  // BLOG-STD-001: chrome strings from the ecosystem SSOT (see blogUiStrings.js);
  // absent `strings` → English defaults so existing callers are byte-identical.
  const s = strings ? { ...DEFAULT_BLOG_UI_STRINGS, ...strings } : DEFAULT_BLOG_UI_STRINGS;
  const resolvedRelatedPostsHeading = relatedPostsHeading ?? s.relatedPostsHeading;
  const resolvedReadingMinutesLabel = readingMinutesLabel ?? s.readingMinutesLabel;
  const resolvedPostTagsLabel = s.postTagsLabel;
  const { state, data, related, relatedUnavailable, error, retry, retryRelated } = useBlogPost({
    client,
    slug,
    locale,
    initialData,
    initialRelated,
    showRelatedPosts,
  });

  if (state === STATE_LOADING) return loadingFallback;
  if (state === STATE_NOT_FOUND) return notFoundFallback || null;
  if (state === STATE_ERROR) {
    if (typeof errorFallback === 'function') return errorFallback({ error, retry });
    return (
      <section className="section-marketing" role="alert" aria-live="assertive">
        <h2 className="section-heading">{s.errorHeading}</h2>
        <p className="section-description">{s.errorBodyPost}</p>
        <button type="button" className="btn btn-marketing-primary" onClick={retry}>
          {s.retry}
        </button>
      </section>
    );
  }

  // In the ready state `data` is always a truthy object (set only from a
  // non-null resolved post; a null resolution routes to not_found above), so
  // `data?.post || data` always yields a truthy value — no `|| {}` fallback
  // is reachable here.
  const post = data?.post || data;
  // PROTECTOR-MKT-002 Phase I R11 (2026-05-25): emit BlogPosting JSON-LD for
  // Google Rich Results. Prefers `post.schemaArticleJson` (authored upstream
  // by PublishPuffin's metadata step) and falls back to a minimal canonical
  // BlogPosting derived from the post fields. Single shared primitive serves
  // every tenant blog page — Gen-0 factory-first.
  const blogPostingJsonLd = buildBlogPostingJsonLd(post);

  // Every optional slot in the article is pre-resolved to a node (or nothing)
  // here, so the returned tree below reads as the article's structure rather
  // than as eight stacked ternaries.
  const jsonLdScript = blogPostingJsonLd ? (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPostingJsonLd) }}
    />
  ) : null;

  const readingProgress = showReadingProgress ? (
    <ReadingProgress ariaLabel={s.readingProgressLabel} />
  ) : null;

  const authorMeta = post.authorName ? (
    <span className="post-author" itemProp="author">
      {post.authorName}
    </span>
  ) : null;

  const publishedMeta = post.publishedAt ? (
    <time className="post-published" dateTime={post.publishedAt} itemProp="datePublished">
      {formatPublishedDate(post.publishedAt, locale)}
    </time>
  ) : null;

  const readingMeta =
    typeof post.readingMinutes === 'number' && resolvedReadingMinutesLabel ? (
      <span className="post-reading">
        {resolvedReadingMinutesLabel.replace('{{minutes}}', String(post.readingMinutes))}
      </span>
    ) : null;

  // Body precedence: a consumer-supplied renderer wins; then sanitized HTML;
  // then a raw-markdown fallback; then nothing.
  const postBody =
    typeof renderBody === 'function' ? (
      renderBody(post)
    ) : post.bodyHtml ? (
      <div
        className="post-content"
        // The body arrives SANITIZED by the ONE blog content policy (`@puffin-core/html-sanitizer`), applied on the
        // server where a post is stored (every write, every sync) AND where it is served
        // (`MarketingContentResolverService.resolveBlogPost` — the SSR seed, the public read API and the feed), so a
        // row stored before the policy existed is served clean too. The browser never carries the purifier.
        dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
      />
    ) : post.bodyMarkdown ? (
      <pre className="post-markdown-fallback">{post.bodyMarkdown}</pre>
    ) : null;

  const relatedRefusal =
    showRelatedPosts && relatedUnavailable ? (
      <aside className="post-related" aria-label={resolvedRelatedPostsHeading}>
        <h2 className="post-related-heading section-heading">{resolvedRelatedPostsHeading}</h2>
        <p className="section-description" role="alert">
          {s.relatedUnavailable}
        </p>
        <button type="button" className="btn btn-marketing-primary" onClick={retryRelated}>
          {s.retry}
        </button>
      </aside>
    ) : null;

  const relatedPanel =
    showRelatedPosts && !relatedUnavailable && related.length > 0 ? (
      <RelatedPosts
        candidates={related}
        currentSlug={post.slug || slug}
        heading={resolvedRelatedPostsHeading}
        hrefPrefix={hrefPrefix}
        readingMinutesLabel={resolvedReadingMinutesLabel}
      />
    ) : null;

  // TAG-VOCAB-001 — the tags a reader (and a crawler) can follow to related posts.
  //
  // Reads `post.tags` (`{slug, name}` terms), NOT `post.tagSlugs`: the label must be the stored
  // display name. De-slugifying would render `ai-receptionist` as "Ai Receptionist" and
  // `b2b-pricing` as "B2b Pricing", mangling casing the corpus deliberately carries.
  //
  // Every chip resolves to a real page with real company: the corpus was collapsed 10,194 → 2,643
  // tags and capped at 5 per post, so no tag here is a one-off phrase invented for this article.
  // `rel="tag"` is the long-standing microformat for exactly this relationship.
  const postTagTerms = Array.isArray(post.tags)
    ? post.tags.filter((term) => term && term.slug && term.name)
    : [];
  const postTags =
    postTagTerms.length > 0 ? (
      <nav className="post-tags" aria-label={resolvedPostTagsLabel}>
        <h2 className="post-tags-label">{resolvedPostTagsLabel}</h2>
        <ul className="post-tags-list">
          {postTagTerms.map((term) => (
            <li key={term.slug}>
              <a className="post-tag-chip" href={`${hrefPrefix}/tag/${term.slug}`} rel="tag">
                {term.name}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    ) : null;

  return (
    <>
      {jsonLdScript}
      {readingProgress}
      <article className="post-detail" itemScope itemType="https://schema.org/Article">
        <header className="post-header">
          <h1 className="post-title" itemProp="headline">
            {post.title}
          </h1>
          <div className="post-meta">
            {authorMeta}
            {publishedMeta}
            {readingMeta}
          </div>
          <BlogPostFeaturedMedia src={post.featuredImageUrl} title={post.title} />
        </header>
        <div className="post-body" itemProp="articleBody">
          {postBody}
        </div>
        {postTags}
        {/* BLOG-STD-001: canonical is emitted (absolute) in <head> by the
            prerender harness (buildBlogHead) — the single source of truth. The
            in-body relative canonical was removed to avoid a conflicting second
            canonical in the prerendered document. */}
      </article>
      {relatedPanel}
      {relatedRefusal}
    </>
  );
}

BlogPostPage.propTypes = {
  client: PropTypes.shape({
    resolveBlogPost: PropTypes.func.isRequired,
    listBlogPosts: PropTypes.func.isRequired,
  }).isRequired,
  slug: PropTypes.string.isRequired,
  locale: PropTypes.string,
  initialData: PropTypes.object,
  initialRelated: PropTypes.array,
  loadingFallback: PropTypes.node,
  notFoundFallback: PropTypes.node,
  errorFallback: PropTypes.func,
  hrefPrefix: PropTypes.string,
  readingMinutesLabel: PropTypes.string,
  showReadingProgress: PropTypes.bool,
  showRelatedPosts: PropTypes.bool,
  relatedPostsHeading: PropTypes.string,
  renderBody: PropTypes.func,
  strings: PropTypes.object,
};
